import * as THREE from "three";

/**
 * See-through scenery over the build plots (Kevin, 10-07: "make the plots aware of the models around them and apply
 * opacity so you can see behind, so the placing icons sit on the ground rather than on top of everything").
 *
 * The plots are drawn ON the ground now (depth-tested, placementPalette.ts), so a tree, rock or prop in front hides
 * them. While plots are shown, every prop material (HqMaterials, not the ground, towers or the fortress — towers fade
 * whole through TowerOcclusion) thins out where it covers a plot on screen and stands in front of it: a soft disc
 * round each plot's screen position, only for fragments nearer the camera than the plot. The thinning is an ordered
 * dither (the merged prop meshes are opaque and must stay so: real blending would mis-sort their own leaves), which at
 * phone pixel density reads as translucency. Shadows are untouched. Presentation only.
 */
export class SeeThrough {
  /** Plots the shader tests (uniform array size): more than any map's legal cells in practice; extras are dropped. */
  static readonly Max = 64;
  /** The see-through disc's radius round a plot (world units: a plot is 0.76 across) and its soft edge (fraction). */
  static readonly Radius = 0.62;
  /** How much of a covering prop is left (0 gone, 1 solid) at the disc's centre. */
  static readonly Keep = 0.28;
  /** A fragment must be this much nearer the camera than the plot to thin (world units): the plot's own grass stays. */
  static readonly Margin = 0.45;
  /** Fade in and out per second when the plots come and go. */
  static readonly Speed = 6;

  static readonly uniforms = {
    uSeeN: { value: 0 },
    uSee: { value: Array.from({ length: SeeThrough.Max }, () => new THREE.Vector4()) },
    uSeeAspect: { value: 1 },
    uSeeK: { value: 0 },
  };
  private static points: THREE.Vector3[] = [];
  private static shown = false;
  private static readonly v = new THREE.Vector3();
  private static readonly up = new THREE.Vector3();

  /** The plots to keep in view (world positions on the ground); empty when placement ends. */
  static setPoints(points: readonly THREE.Vector3[]): void {
    SeeThrough.shown = points.length > 0;
    if (SeeThrough.shown) SeeThrough.points = points.slice(0, SeeThrough.Max).map((p) => p.clone());
  }
  /** What the tower fader should also keep in view (TowerOcclusion targets). */
  static targets(): readonly THREE.Vector3[] { return SeeThrough.shown ? SeeThrough.points : []; }

  /** Per frame: eases the effect in or out and writes each plot's screen disc and depth for `camera`. */
  static update(dt: number, camera: THREE.PerspectiveCamera): void {
    const u = SeeThrough.uniforms;
    const want = SeeThrough.shown ? 1 : 0;
    u.uSeeK.value += Math.sign(want - u.uSeeK.value) * Math.min(Math.abs(want - u.uSeeK.value), SeeThrough.Speed * dt);
    if (u.uSeeK.value <= 0) { u.uSeeN.value = 0; return; }
    camera.updateMatrixWorld();
    u.uSeeAspect.value = camera.aspect;
    SeeThrough.up.setFromMatrixColumn(camera.matrixWorld, 1).normalize();
    let n = 0;
    for (const p of SeeThrough.points) {
      const c = SeeThrough.v.copy(p).applyMatrix4(camera.matrixWorldInverse);
      const depth = -c.z;
      if (depth <= camera.near) continue;
      const ndc = SeeThrough.v.copy(p).project(camera);
      const x = ndc.x, y = ndc.y;
      if (x < -1.3 || x > 1.3 || y < -1.3 || y > 1.3) continue;
      const top = SeeThrough.v.copy(p).addScaledVector(SeeThrough.up, SeeThrough.Radius).project(camera);
      u.uSee.value[n++].set(x, y, Math.max(1e-4, Math.abs(top.y - y)), depth);
    }
    u.uSeeN.value = n;
  }

  /** Patches a lit prop material's shaders (call from its onBeforeCompile, before VfxLights.inject). */
  static inject(sh: THREE.WebGLProgramParametersWithUniforms): void {
    const u = SeeThrough.uniforms;
    sh.uniforms.uSeeN = u.uSeeN; sh.uniforms.uSee = u.uSee; sh.uniforms.uSeeAspect = u.uSeeAspect; sh.uniforms.uSeeK = u.uSeeK;
    sh.vertexShader = sh.vertexShader
      .replace("#include <common>", "#include <common>\nvarying vec4 vSeeClip;")
      .replace("#include <project_vertex>", "#include <project_vertex>\n  vSeeClip = gl_Position;");
    sh.fragmentShader = sh.fragmentShader
      .replace("#include <common>", `#include <common>
varying vec4 vSeeClip;
uniform int uSeeN;
uniform vec4 uSee[${SeeThrough.Max}];
uniform float uSeeAspect;
uniform float uSeeK;`)
      .replace("#include <clipping_planes_fragment>", `#include <clipping_planes_fragment>
  if ( uSeeN > 0 ) {
    vec2 seeNdc = vSeeClip.xy / vSeeClip.w;
    float seeDepth = vViewPosition.z;
    float thin = 0.0;
    for ( int i = 0; i < ${SeeThrough.Max}; i ++ ) {
      if ( i >= uSeeN ) break;
      vec4 s = uSee[ i ];
      if ( seeDepth > s.w - ${SeeThrough.Margin.toFixed(2)} ) continue;
      vec2 d = vec2( ( seeNdc.x - s.x ) * uSeeAspect, seeNdc.y - s.y ) / s.z;
      thin = max( thin, 1.0 - smoothstep( 0.55, 1.0, length( d ) ) );
    }
    float keep = 1.0 - thin * uSeeK * ${(1 - SeeThrough.Keep).toFixed(2)};
    // 4×4 ordered dither: keep that share of the pixels
    ivec2 q = ivec2( mod( gl_FragCoord.xy, 4.0 ) );
    int b = q.x + q.y * 4;
    float bayer = b == 0 ? 0.0 : b == 1 ? 8.0 : b == 2 ? 2.0 : b == 3 ? 10.0 : b == 4 ? 12.0 : b == 5 ? 4.0 : b == 6 ? 14.0 : b == 7 ? 6.0
      : b == 8 ? 3.0 : b == 9 ? 11.0 : b == 10 ? 1.0 : b == 11 ? 9.0 : b == 12 ? 15.0 : b == 13 ? 7.0 : b == 14 ? 13.0 : 5.0;
    if ( keep < 1.0 && keep <= ( bayer + 0.5 ) / 16.0 ) discard;
  }`);
  }
}
